import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    try {
      document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
      cache = document.cookie.includes(k);
      document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    } catch {
      cache = false;
    }
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const idx = c.trim().indexOf('=');
          const name = idx !== -1 ? c.trim().slice(0, idx) : c.trim();
          const rest = idx !== -1 ? c.trim().slice(idx + 1) : '';
          return { name: name.trim(), value: decodeURIComponent(rest) };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    if (typeof window === 'undefined') return [];
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: Record<string, unknown>) => {
  if (typeof document === 'undefined') return;
  try {
    let s = `${name}=${encodeURIComponent(value)}; Path=${(options?.path as string) || '/'}; SameSite=None; Secure; Partitioned`;
    if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
    if (options?.domain) s += `; Domain=${options.domain}`;
    if (options?.expires) s += `; Expires=${new Date(options.expires as string).toUTCString()}`;
    document.cookie = s;
  } catch {}
};

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          if (canUseCookies()) {
            cookiesToSet.forEach(({ name, value, options }) => {
              if (value) {
                setCookie(name, value, options as Record<string, unknown>);
              } else {
                try {
                  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
                } catch {}
              }
            });
          } else {
            cookiesToSet.forEach(({ name, value, options }) => {
              try {
                value
                  ? localStorage.setItem(`${PFX}${name}`, value)
                  : localStorage.removeItem(`${PFX}${name}`);
              } catch {}
              if (value) setCookie(name, value, options as Record<string, unknown>);
            });
          }
        },
      },
    }
  );
}
